/* eslint-disable class-methods-use-this */
/* global chiselScripts */

const $ = window.jQuery;

export default new class Configurator {
  constructor() {
    this.selectors = {
      trigger: '.js-configurator-trigger',
      modal: '.c-configurator-modal',
      backdrop: '.js-configurator-backdrop',
      close: '.js-configurator-close',
      startBtn: '.js-configurator-start',
      panel: '.js-configurator-panel',
      formWrap: '.js-configurator-form-wrap',
      gfBack: '.js-configurator-gf-back',
      gfBackToPage1: '.js-configurator-gf-back-to-page1',
      fallbackSpinner: '.js-configurator-spinner-fallback',
      brandField: '.is-brand-field select',
      modelField: '.is-model-field select',
      selectMatch: '.js-configurator-select-match',
      downloadPdf: '.js-configurator-download-pdf',
      restart: '.js-configurator-restart',
    };

    this.globals = chiselScripts.configurator;

    this.classes = {
      open: 'is-open',
      locked: 'is-locked',
      activePanel: 'is-active',
    };

    this.events = {
      start: 'configurator:start',
      back: 'configurator:back',
    };

    this.currentGfPage = 0;
    this.isConfirmationActive = false;
    this.spinnerDelay = 300;
    this.spinnerDelayTimeout = null;
    this.spinnerPaintTimeout = null;

    $(document).ready(() => this.init());
  }

  init() {
    if (!$(this.selectors.trigger).length) return;

    this.bindTriggers();
    this.bindClose();
    this.bindStartButton();
    this.bindCustomEvents();
    this.bindGfPageChange();
    this.bindKeyboard();
    this.bindBrandModel();
    this.bindSelectMatch();
    this.bindDownloadPdf();
    this.bindRestart();
    this.bindDynamics();
    this.maybeAutoOpen();
  }

  bindRestart() {
    $(document).on('click', this.selectors.restart, e => {
      e.preventDefault();
      this.reloadForm();
      this.showPanel('start');
    });
  }

  maybeAutoOpen() {
    const params = new URLSearchParams(window.location.search);
    if (params.get('configurator') !== 'open') return;

    // Remove the param from the URL without reloading
    const url = new URL(window.location.href);
    url.searchParams.delete('configurator');
    window.history.replaceState(null, '', url.toString());

    this.open();
  }

  bindTriggers() {
    $(document).on('click', this.selectors.trigger, e => {
      e.preventDefault();
      this.open();
    });
  }

  open() {
    const $modal = $(this.selectors.modal);

    $modal.addClass(this.classes.open).attr('aria-hidden', 'false');

    $(this.selectors.trigger).prop('disabled', true);

    $('html').addClass(this.classes.locked);
    this.showPanel('start');
    this.currentGfPage = 0;
    this.syncSelectedMatchState();
    this.syncConfigurationIdState();
    $modal.find(this.selectors.close).trigger('focus');
  }

  close() {
    const $modal = $(this.selectors.modal);

    $modal.removeClass(this.classes.open).attr('aria-hidden', 'true');

    $('html').removeClass(this.classes.locked);
    this.clearSpinnerTimers();
    this.hideSpinner();

    this.reloadForm();
  }

  getForm() {
    return $(this.selectors.formWrap).find('form');
  }

  getFormId() {
    const $form = this.getForm();

    if (!$form.length) return '';

    return (
      $form.attr('id')?.replace('gform_', '') ||
      $form
        .find('input[name^="is_submit_"]')
        .attr('name')
        ?.replace('is_submit_', '') ||
      $form
        .find('[name^="gform_target_page_number_"]')
        .attr('name')
        ?.replace('gform_target_page_number_', '') ||
      ''
    );
  }

  getGravityFormsAjaxHelper() {
    return window.chiselGravityFormsAjax || null;
  }

  // Fetch a fresh PDF nonce. The localized pdfNonce goes stale when the page is
  // served from a full-page cache, so resolve a current one at request time.
  getPdfNonce() {
    const fallback = this.globals.pdfNonce || '';

    return Promise.resolve(
      $.ajax({
        url: this.globals.ajaxUrl,
        type: 'POST',
        data: { action: 'configurator_pdf_nonce' },
      }),
    )
      .then(response => response?.data?.nonce || fallback)
      .catch(() => fallback);
  }

  submitCurrentForm({ targetPage = null, sourcePage = null } = {}) {
    const $form = this.getForm();
    const form = $form.get(0);
    const formId = this.getFormId();
    const helper = this.getGravityFormsAjaxHelper();

    if (!form || !formId || !helper) {
      this.hideSpinner();
      return Promise.resolve(null);
    }

    const request =
      targetPage === null
        ? helper.submit(form)
        : helper.goToPage(form, targetPage, sourcePage);

    return Promise.resolve(request)
      .then(result => {
        if (!result || result.success === false) {
          this.hideSpinner();
        }

        return result;
      })
      .catch(() => {
        this.hideSpinner();
        return null;
      });
  }

  getSelectedMatchStorageKey(formId = this.getFormId()) {
    return formId ? `configurator:selected-match:${formId}` : '';
  }

  getConfigurationIdStorageKey(formId = this.getFormId()) {
    return formId ? `configurator:configuration-id:${formId}` : '';
  }

  readSelectedMatchIndex(formId = this.getFormId()) {
    const storageKey = this.getSelectedMatchStorageKey(formId);

    if (!storageKey) return '';

    try {
      return window.sessionStorage.getItem(storageKey) ?? '';
    } catch (error) {
      return '';
    }
  }

  writeSelectedMatchIndex(value, formId = this.getFormId()) {
    const storageKey = this.getSelectedMatchStorageKey(formId);

    if (!storageKey) return;

    try {
      if (value === '' || value === null || typeof value === 'undefined') {
        window.sessionStorage.removeItem(storageKey);
        return;
      }

      window.sessionStorage.setItem(storageKey, String(value));
    } catch (error) {
      // Ignore storage errors and let the hidden input carry the value.
    }
  }

  readConfigurationId(formId = this.getFormId()) {
    const storageKey = this.getConfigurationIdStorageKey(formId);

    if (!storageKey) return '';

    try {
      return window.sessionStorage.getItem(storageKey) ?? '';
    } catch (error) {
      return '';
    }
  }

  writeConfigurationId(value, formId = this.getFormId()) {
    const storageKey = this.getConfigurationIdStorageKey(formId);

    if (!storageKey) return;

    try {
      if (value === '' || value === null || typeof value === 'undefined') {
        window.sessionStorage.removeItem(storageKey);
        return;
      }

      window.sessionStorage.setItem(storageKey, String(value));
    } catch (error) {
      // Ignore storage errors and let the hidden input carry the value.
    }
  }

  ensurePersistentField(name, value = '') {
    const $form = this.getForm();

    if (!$form.length) return $();

    let $field = $form.children(
      `[data-configurator-persistent-field="${name}"]`,
    );

    if (!$field.length) {
      $field = $('<input />', {
        type: 'hidden',
        name,
      })
        .attr('data-configurator-persistent-field', name)
        .prependTo($form);
    }

    $field.val(value);

    return $field;
  }

  syncSelectedMatchInputs(value = this.readSelectedMatchIndex()) {
    const normalizedValue =
      value === null || typeof value === 'undefined' ? '' : String(value);
    const $form = this.getForm();

    if (!$form.length) return;

    this.ensurePersistentField('cf_selected_match_index', normalizedValue);

    const $pageField = $form.find('#cf_selected_match_index');
    if ($pageField.length) {
      $pageField.val(normalizedValue);
    }
  }

  syncSelectedMatchState() {
    const $form = this.getForm();

    if (!$form.length) return;

    const pageValue = $form.find('#cf_selected_match_index').val();
    const storedValue = this.readSelectedMatchIndex();
    const value =
      pageValue !== undefined && pageValue !== null && pageValue !== ''
        ? pageValue
        : storedValue;

    if (value !== '' && value !== undefined && value !== null) {
      this.writeSelectedMatchIndex(value);
      this.syncSelectedMatchInputs(value);
      return;
    }

    this.syncSelectedMatchInputs('');
  }

  syncConfigurationIdInputs(value = this.readConfigurationId()) {
    const normalizedValue =
      value === null || typeof value === 'undefined' ? '' : String(value);
    const $form = this.getForm();

    if (!$form.length) return;

    this.ensurePersistentField('cf_configuration_id', normalizedValue);

    const $pageField = $form.find('#cf_configuration_id');
    if ($pageField.length) {
      $pageField.val(normalizedValue);
    }
  }

  syncConfigurationIdState() {
    const $form = this.getForm();

    if (!$form.length) return;

    const pageValue = $form.find('#cf_configuration_id').val();
    const storedValue = this.readConfigurationId();
    const value =
      pageValue !== undefined && pageValue !== null && pageValue !== ''
        ? pageValue
        : storedValue;

    if (value !== '' && value !== undefined && value !== null) {
      this.writeConfigurationId(value);
      this.syncConfigurationIdInputs(value);
      return;
    }

    this.syncConfigurationIdInputs('');
  }

  clearConfigurationId(formId = this.getFormId()) {
    this.writeConfigurationId('', formId);
    this.syncConfigurationIdInputs('');
  }

  clearSelectedMatchIndex(formId = this.getFormId()) {
    this.writeSelectedMatchIndex('', formId);
    this.syncSelectedMatchInputs('');
  }

  resetGfToPage1() {
    this.clearSelectedMatchIndex();
    this.clearConfigurationId();

    if (this.currentGfPage <= 1) return;

    const $form = this.getForm();
    const formId = $form
      .find('[name^="gform_target_page_number_"]')
      .attr('name')
      ?.replace('gform_target_page_number_', '');

    if (formId) {
      this.showSpinner();
      this.submitCurrentForm({
        targetPage: 1,
        sourcePage: this.currentGfPage,
      });
    }

    this.currentGfPage = 0;
    this.gfRealTotal = null;
    this.progressBarObserver = null;
  }

  reloadForm() {
    this.clearSelectedMatchIndex();
    this.clearConfigurationId();

    const $wrap = $(this.selectors.formWrap);

    $.ajax({
      url: this.globals.ajaxUrl,
      type: 'POST',
      data: {
        action: 'configurator_reload_form',
        nonce: this.globals.reloadFormNonce || '',
      },
      success: ({ success, data }) => {
        if (success && data && data.html) {
          // Preserve the footer note that lives outside the GF wrapper.
          const $note = $wrap.find('.c-configurator-modal__form-note').detach();

          $wrap.html(data.html);

          if ($note.length) {
            $wrap.append($note);
          }

          this.isConfirmationActive = false;
          this.currentGfPage = 0;
          this.gfRealTotal = null;

          if (this.progressBarObserver) {
            this.progressBarObserver.disconnect();
            this.progressBarObserver = null;
          }

          // Let GF re-initialize its bindings on the fresh markup.
          const formId = this.getFormId();

          if (formId && window.gformInitSpinner) {
            window.gformInitSpinner(formId);
          }

          const gravityFormsAjax = this.getGravityFormsAjaxHelper();

          if (gravityFormsAjax) {
            gravityFormsAjax.triggerPostRender(formId, 1);
          } else {
            $(document).trigger('gform_post_render', [formId, 1]);
          }

          $(this.selectors.trigger).prop('disabled', false);
        }
      },
      error: () => {
        // Fallback: reload the page if AJAX fails.
        window.location.reload();
      },
    });
  }

  bindClose() {
    $(document).on('click', this.selectors.close, () => this.close());
    $(document).on('click', this.selectors.backdrop, () => this.close());
  }

  bindKeyboard() {
    $(document).on('keydown', e => {
      if (
        e.key === 'Escape' &&
        $(this.selectors.modal).hasClass(this.classes.open)
      ) {
        this.close();
      }
    });
  }

  showPanel(name) {
    const $panels = $(this.selectors.panel);

    $panels.each((_, el) => {
      const $panel = $(el);
      const isTarget = $panel.data('panel') === name;

      $panel
        .toggleClass(this.classes.activePanel, isTarget)
        .attr('aria-hidden', isTarget ? 'false' : 'true');
    });

    $(this.selectors.modal)
      .find('.c-configurator-modal__dialog')
      .scrollTop(0);
  }

  getSpinnerAnchor() {
    const $wrap = $(this.selectors.formWrap);

    // On GF pages: place after next button or submit button (same as native GF)
    const $next = $wrap
      .find('.gform_next_button:visible, .gform_button:visible')
      .first();

    if ($next.length) {
      return $next;
    }

    // Next button exists but is hidden (e.g. last page): anchor to the form footer
    const $footer = $wrap.find('.gform_footer, .gform_page_footer').first();
    if ($footer.length) {
      return $footer;
    }

    // On start panel: place after the start button
    return $(this.selectors.modal)
      .find(this.selectors.startBtn)
      .first();
  }

  showSpinner() {
    this.hideSpinner();

    const $anchor = this.getSpinnerAnchor();

    if (!$anchor.length) return;

    $('<span />', {
      'data-js-spinner-id': 'gform-ajax-spinner',
      class: 'gform-loader js-configurator-spinner-fallback',
    }).insertAfter($anchor);
  }

  hideSpinner() {
    $(this.selectors.fallbackSpinner).remove();
  }

  clearSpinnerTimers() {
    window.clearTimeout(this.spinnerDelayTimeout);
    window.clearTimeout(this.spinnerPaintTimeout);
  }

  withSpinner(fn) {
    this.clearSpinnerTimers();
    this.showSpinner();

    this.spinnerDelayTimeout = window.setTimeout(() => {
      fn();

      this.spinnerPaintTimeout = window.setTimeout(() => {
        this.hideSpinner();
      }, 150);
    }, this.spinnerDelay);
  }

  bindStartButton() {
    $(document).on('click', this.selectors.startBtn, e => {
      e.preventDefault();
      $(document).trigger(this.events.start);
    });
  }

  bindCustomEvents() {
    $(document).on(this.events.start, () => {
      this.showPanel('form');
      this.currentGfPage = 1;
    });

    $(document).on(this.events.back, () => {
      this.withSpinner(() => {
        this.showPanel('start');
        this.currentGfPage = 0;
        this.gfRealTotal = null;
        if (this.progressBarObserver) {
          this.progressBarObserver.disconnect();
          this.progressBarObserver = null;
        }
        this.clearSelectedMatchIndex();
        this.clearConfigurationId();
      });
    });
  }

  patchProgressBar(currentPage) {
    const $modal = $(this.selectors.modal);
    const $wrapper = $modal.find('[id^="gf_progressbar_wrapper_"]');
    if (!$wrapper.length) return;

    const $count = $wrapper.find('.gf_step_page_count');
    const $bar = $wrapper.find('.gf_progressbar_percentage');

    // Cache only if the DOM value is larger than what we already have (i.e. unpatched/fresh render)
    const domTotal = Number($count.text());
    if (!this.gfRealTotal || domTotal > this.gfRealTotal) {
      this.gfRealTotal = domTotal;
    }
    if (!this.gfRealTotal || this.gfRealTotal < 2) return;

    const applyPatch = page => {
      const visibleTotal = this.gfRealTotal - 1;
      $count.text(visibleTotal);

      const p = page || this.currentGfPage;
      if (p) {
        const pct = Math.round((p / visibleTotal) * 100);
        $bar
          .css('width', `${pct}%`)
          .find('span')
          .text(`${pct}%`);
      }
    };

    applyPatch(currentPage);

    // GF re-renders the form on every page change, so reattach the observer to the fresh node.
    if (this.progressBarObserver) {
      this.progressBarObserver.disconnect();
    }
    if ($count.length) {
      this.progressBarObserver = new MutationObserver(() => {
        const current = Number($count.text());
        if (current === this.gfRealTotal) {
          applyPatch();
        }
      });
      this.progressBarObserver.observe($count[0], {
        childList: true,
        subtree: true,
        characterData: true,
      });
    }
  }

  bindBrandModel() {
    const modelsByBrand = this.globals.modelsByBrands;
    const $wrap = $(this.selectors.formWrap);

    const filterModels = () => {
      const $brand = $wrap.find(this.selectors.brandField);
      const $model = $wrap.find(this.selectors.modelField);
      if (!$brand.length || !$model.length) return;

      const brand = $brand.val();
      const allowed = modelsByBrand[brand] || [];

      if (!brand) {
        $model.prop('disabled', true).val('');
        $model
          .find('option')
          .not('.gf_placeholder')
          .hide();
      } else {
        $model.prop('disabled', false);
        $model
          .find('option')
          .not('.gf_placeholder')
          .each((_, opt) => {
            const $opt = $(opt);
            $opt.toggle(allowed.includes($opt.val()));
          });

        // Reset model if current value is not in the allowed list
        if ($model.val() && !allowed.includes($model.val())) {
          $model.val('');
        }
      }
    };

    $(document).on('change', this.selectors.brandField, filterModels);

    // Run on page render to set initial state
    $(document).on('gform_post_render', () => filterModels());
  }

  bindSelectMatch() {
    $(document).on('click', this.selectors.selectMatch, e => {
      e.preventDefault();

      const $btn = $(e.currentTarget);
      const matchIndex = $btn.data('match-index');

      this.clearConfigurationId();
      this.writeSelectedMatchIndex(matchIndex);
      this.syncSelectedMatchInputs(matchIndex);

      // Reset GF's internal "submission in progress" lock so it doesn't
      // reject our programmatic page advance.
      const formId = this.getFormId();
      if (formId && window.gf_submitting) {
        window.gf_submitting = false;
      }
      if (formId && window[`gf_submitting_${formId}`] !== undefined) {
        window[`gf_submitting_${formId}`] = false;
      }

      const $nextBtn = $(this.selectors.formWrap).find('.gform_next_button');
      if ($nextBtn.length) {
        const gravityFormsAjax = this.getGravityFormsAjaxHelper();

        if (gravityFormsAjax) {
          gravityFormsAjax.handleButtonClick($nextBtn.get(0));
          return;
        }

        $nextBtn.trigger('click');
      }
    });
  }

  bindDownloadPdf() {
    // Click handler - fetch PDF URL via AJAX then open in new tab.
    $(document).on('click', this.selectors.downloadPdf, e => {
      const $btn = $(e.currentTarget);

      if ($btn.prop('disabled')) return;

      // On the confirmation page the button carries a cached URL from a previous
      // successful generation - reuse it without another AJAX round-trip.
      const cachedUrl = $btn.data('pdf-url');
      if (cachedUrl) {
        window.open(cachedUrl, '_blank');
        return;
      }

      // Resolve the configuration_id from the form hidden field
      // or from the button's own data attribute (confirmation merge tag case).
      const configId =
        $btn.data('configuration-id') ||
        $(this.selectors.formWrap)
          .find('#cf_configuration_id, [name="cf_configuration_id"]')
          .first()
          .val() ||
        '';

      if (!configId) return;

      const formPayload = this.getForm().serialize();

      $btn.prop('disabled', true);

      this.getPdfNonce().then(nonce => {
        $.ajax({
          url: this.globals.ajaxUrl,
          type: 'POST',
          data: {
            action: 'configurator_get_pdf',
            nonce,
            configuration_id: configId,
            form_payload: formPayload,
          },
          success: ({ success, data }) => {
            if (success && data && data.url) {
              const { url } = data;
              // Cache the URL on confirmation buttons (those with data-configuration-id
              // and no form wrap available) so repeat clicks skip the AJAX call.
              if (
                $btn.data('configuration-id') &&
                !$(this.selectors.formWrap).find('form').length
              ) {
                $btn.data('pdf-url', url);
              }
              window.open(url, '_blank');
            }
          },
          complete: () => {
            $btn.prop('disabled', false);
          },
        });
      });
    });
  }

  updatePageClass(page) {
    const $form = $(this.selectors.formWrap).find('form');
    if (!$form.length || !page) return;

    // Remove any previous is-page-N class, then add the current one.
    $form.removeClass((_, cls) =>
      (cls.match(/\bis-page-\d+\b/g) || []).join(' '),
    );
    $form.addClass(`is-page-${page}`);
  }

  bindDynamics() {
    const config = this.globals.dynamics;

    if (!config) return;

    const captureSnapshot = formEl => {
      const $form = formEl ? $(formEl) : this.getForm();
      if (!$form.length) return;

      const snapshot = {};
      $form.find('[name]').each((_, el) => {
        const $el = $(el);
        const name = $el.attr('name');
        if (!name) return;
        if ($el.is(':checkbox') || $el.is(':radio')) {
          if ($el.is(':checked')) snapshot[name] = $el.val();
        } else {
          snapshot[name] = $el.val() || '';
        }
      });

      this.dynamicsFormSnapshot = snapshot;
    };

    if (window.gform?.utils?.addFilter) {
      window.gform.utils.addFilter('gform/submission/pre_submission', data => {
        const formId = this.getFormId();
        const submissionType = window.gform?.submission?.SUBMISSION_TYPE_SUBMIT;

        if (
          data?.form &&
          submissionType &&
          data.submissionType === submissionType &&
          String(data.form.dataset.formid || '') === String(formId || '')
        ) {
          captureSnapshot(data.form);
        }

        return data;
      });
    } else {
      $(document).on('submit', `${this.selectors.formWrap} form`, e => {
        captureSnapshot(e.currentTarget);
      });
    }

    $(document).on('gform_confirmation_loaded', () => {
      const snapshot = this.dynamicsFormSnapshot;

      if (!snapshot || typeof window.d365mktformcapture === 'undefined') return;

      const submitToDynamics = pdfUrl => {
        const mappings = config.mappings.map(m => {
          if (m.countryField) {
            const countryVal = snapshot[m.FormFieldName] || '';
            return {
              FormFieldName: m.FormFieldName,
              DataverseFieldName: m.DataverseFieldName,
              DataverseFieldValue: countryVal
                ? JSON.stringify({ Id: countryVal, LogicalName: 'pyl_country' })
                : '',
            };
          }
          if (m.checkboxField) {
            const checked = snapshot[`${m.FormFieldName}.1`] === '1';
            return {
              DataverseFieldName: m.DataverseFieldName,
              DataverseFieldValue: checked ? '1' : '0',
            };
          }
          return m;
        });

        if (pdfUrl) {
          mappings.push({
            DataverseFieldName: 'ymi_comments',
            DataverseFieldValue: pdfUrl,
          });
        }

        const $tmpForm = $('<form />');

        Object.entries(snapshot).forEach(([name, value]) => {
          $('<input />', { type: 'hidden', name, value }).appendTo($tmpForm);
        });

        $tmpForm.appendTo('body');

        try {
          const serialized = window.d365mktformcapture.serializeForm(
            $tmpForm[0],
            mappings,
          );
          const payload = serialized.SerializedForm.build();

          window.d365mktformcapture.submitForm(
            { FormId: config.formId, FormApiUrl: config.formApiUrl },
            payload,
          );
        } catch (err) {
          // eslint-disable-next-line no-console
          console.error('Dynamics form capture error:', err);
        } finally {
          $tmpForm.remove();
          this.dynamicsFormSnapshot = null;
        }
      };

      // Fetch the PDF URL using the configuration_id from the thank-you button,
      // then submit to Dynamics with the URL included.
      const $downloadBtn = $(this.selectors.downloadPdf).first();
      const configId = $downloadBtn.data('configuration-id') || '';

      if (!configId) {
        submitToDynamics('');
        return;
      }

      this.getPdfNonce().then(nonce => {
        $.ajax({
          url: this.globals.ajaxUrl,
          type: 'POST',
          data: {
            action: 'configurator_get_pdf',
            nonce,
            configuration_id: configId,
            form_payload: snapshot
              ? Object.entries(snapshot)
                  .map(
                    ([k, v]) =>
                      `${encodeURIComponent(k)}=${encodeURIComponent(v)}`,
                  )
                  .join('&')
              : '',
          },
          success: response => {
            submitToDynamics(
              response.success && response.data && response.data.url
                ? response.data.url
                : '',
            );
          },
          error: () => {
            submitToDynamics('');
          },
        });
      });
    });
  }

  wrapValueUnitPairs() {
    const $fields = $(this.selectors.formWrap).find('.gform_fields');

    $fields.each((_, fieldList) => {
      const $items = $(fieldList).children('.gfield');

      $items.each((i, el) => {
        const $value = $(el);

        if (!$value.hasClass('is-cf-value')) return;

        const $next = $items.eq(i + 1);

        if (!$next.hasClass('is-cf-unit')) return;

        // Skip if already wrapped
        if ($value.parent().hasClass('gfield-cf-value-unit-wrap')) return;

        $value.add($next).wrapAll('<div class="gfield-cf-value-unit-wrap" />');
      });
    });
  }

  bindGfPageChange() {
    $(document).on(
      'gform_post_render gform_page_loaded',
      (e, formId, currentPage) => {
        if (currentPage) {
          this.currentGfPage = currentPage;
        }
        this.hideSpinner();
        this.patchProgressBar(currentPage);
        this.updatePageClass(currentPage);
        this.syncSelectedMatchState();
        this.syncConfigurationIdState();
        this.wrapValueUnitPairs();
      },
    );

    $(document).on('gform_confirmation_loaded', (e, formId) => {
      this.isConfirmationActive = true;
      this.clearSelectedMatchIndex(formId);
      this.clearConfigurationId(formId);
    });

    $(document).on('click', this.selectors.gfBackToPage1, e => {
      e.preventDefault();

      const $form = this.getForm();
      const formId = $form
        .find('[name^="gform_target_page_number_"]')
        .attr('name')
        ?.replace('gform_target_page_number_', '');

      if (formId) {
        this.clearSelectedMatchIndex(formId);
        this.clearConfigurationId(formId);
        this.showSpinner();
        this.submitCurrentForm({
          targetPage: 1,
          sourcePage: this.currentGfPage,
        });
      }
    });

    $(document).on('click', this.selectors.gfBack, e => {
      e.preventDefault();

      if (this.currentGfPage <= 1) {
        $(document).trigger(this.events.back);
      } else {
        // GF navigates by setting the target page hidden field and submitting.
        // The native prev button is display:none so clicking it won't work.
        // eslint-disable-next-line no-unused-vars
        const $form = this.getForm();
        const formId = this.getFormId();

        if (formId) {
          const targetPage = this.currentGfPage - 1;

          if (targetPage <= 1) {
            this.clearSelectedMatchIndex(formId);
            this.clearConfigurationId(formId);
          }

          this.showSpinner();
          this.submitCurrentForm({
            targetPage,
            sourcePage: this.currentGfPage,
          });
        }
      }
    });
  }
}();



// WEBPACK FOOTER //
// ./wp/wp-content/themes/yanmar-chisel/src/scripts/modules/configurator.js